export const translations = {
  en: {
    common: {
      cancel: 'Cancel',
      save: 'Save',
      saveChanges: 'Save Changes',
      remove: 'Remove',
      edit: 'Edit',
      deleteShort: 'Del',
      allCategories: 'All Categories',
      allTypes: 'All Types',
      income: 'Income',
      expense: 'Expense',
      type: 'Type',
      category: 'Category',
      amount: 'Amount',
      date: 'Date',
      description: 'Description',
      actions: 'Actions',
      monthly: 'Monthly',
      weekly: 'Weekly',
      yearly: 'Yearly',
      active: 'Active',
      paused: 'Paused',
      next: 'Next',
    },
    sidebar: {
      appName: 'FinFlow',
      appSub: 'Finance Tracker',
      menu: 'Menu',
      nav: {
        dashboard: 'Dashboard',
        transactions: 'Transactions',
        insights: 'Insights',
        budgets: 'Budgets',
        recurring: 'Recurring',
        aiInsights: 'AI Insights',
      },
      role: 'Role',
      admin: 'Admin',
      viewer: 'Viewer',
      adminAccess: 'Admin Access',
      viewOnly: 'View Only',
      language: 'Language',
      languageNames: {
        en: 'English',
        hi: 'Hindi',
        te: 'Telugu',
      },
    },
    topbar: {
      pages: {
        dashboard: { title: 'Dashboard', sub: 'April 2026 · Financial overview' },
        transactions: { title: 'Transactions', sub: 'Manage your transactions' },
        insights: { title: 'Insights', sub: 'Spending analysis & patterns' },
        budgets: { title: 'Budget Goals', sub: 'Track your monthly spending limits' },
        recurring: { title: 'Recurring', sub: 'Manage subscriptions & recurring payments' },
        aiInsights: { title: 'AI Advisor', sub: 'Chat with your AI financial advisor' },
      },
      export: 'Export',
      addTransaction: 'Add Transaction',
    },
    toasts: {
      roleAdmin: 'Switched to Admin mode',
      roleViewer: 'Switched to Viewer mode',
      csvExport: 'CSV exported',
      txUpdated: 'Transaction updated',
      txAdded: 'Transaction added',
      txDeleted: 'Transaction deleted',
      budgetAdded: 'Budget goal added',
      budgetRemoved: 'Budget removed',
      recurringAdded: 'Recurring transaction added',
      recurringPaused: 'Paused',
      recurringResumed: 'Resumed',
      recurringRemoved: 'Removed',
    },
    dashboard: {
      labels: {
        balance: 'Balance',
        income: 'Income',
        expense: 'Expense',
        savingsRate: 'Savings Rate',
        healthScore: 'Health Score',
      },
      terms: {
        balance: 'Balance is the money left after subtracting your expenses from your income.',
        income: 'Income is money coming in, like salary, freelance payments, or returns.',
        expense: 'Expenses are money going out, like bills, food, transport, and shopping.',
        savingsRate: 'Savings rate is the percentage of your income that you keep after expenses.',
      },
      cardNotes: {
        balancePositive: 'Positive cash flow',
        balanceNegative: 'Negative cash flow',
        incomeNote: 'Money coming in this month',
        expenseNote: 'Money spent this month',
        savingsPositive: 'Building savings',
        savingsNegative: 'Savings under pressure',
        healthNote: 'Based on income, spending, and savings',
      },
      interpretations: {
        balancePositive: 'You earned more than you spent this month.',
        balanceNegative: 'You spent more than you earned this month.',
        incomeStrong: 'Strong income this month gives you flexibility.',
        incomeModest: 'Income is modest this month, so budget carefully.',
        expenseControlled: 'Your spending is controlled relative to income.',
        expenseHigh: 'Spending is high, but still within your income.',
        expenseOver: 'Spending is above income. Try reducing non-essential costs.',
        savingsGreat: "You are saving more than half your income. Great job!",
        savingsGood: 'You are saving a good portion of income. Keep it up.',
        savingsLow: 'Your savings are low right now. Look for small spending cuts.',
        savingsNegative: 'You are not saving this month because spending is above income.',
      },
      health: {
        healthy: 'Your finances are healthy.',
        stable: 'Your finances are stable, with room to improve.',
        overspending: 'You are overspending this month.',
      },
      chart: {
        balanceTrend: 'Balance Trend',
        balanceSub: 'Running balance over time',
        spendCategory: 'Spending by Category',
        currentMonth: 'Current month',
        recentTransactions: 'Recent Transactions',
      },
      table: {
        date: 'Date',
        description: 'Description',
        category: 'Category',
        type: 'Type',
        amount: 'Amount',
      },
      legends: {
        balance: 'Balance',
        income: 'Income',
        expenses: 'Expenses',
      },
    },
    transactions: {
      modal: {
        addTitle: 'Add Transaction',
        editTitle: 'Edit Transaction',
        descLabel: 'Description',
        descPlaceholder: 'e.g. Netflix subscription',
        amountLabel: 'Amount (INR)',
        dateLabel: 'Date',
        typeLabel: 'Type',
        categoryLabel: 'Category',
        saveAdd: 'Save Transaction',
      },
      searchPlaceholder: 'Search transactions...',
      sort: {
        newest: 'Newest First',
        oldest: 'Oldest First',
        highest: 'Highest Amount',
        lowest: 'Lowest Amount',
      },
      add: 'Add',
      empty: {
        title: 'No transactions found',
        sub: 'No transactions match your filters.',
      },
      showing: 'Showing {{filtered}} of {{total}} transactions',
      headers: {
        date: 'Date',
        description: 'Description',
        category: 'Category',
        type: 'Type',
        amount: 'Amount',
        actions: 'Actions',
      },
    },
    budgets: {
      modal: {
        title: 'Add Budget Goal',
        category: 'Category',
        allBudgeted: 'All categories budgeted',
        monthlyLimit: 'Monthly Limit (USD)',
        alertAt: 'Alert At (%)',
        save: 'Save Budget',
      },
      empty: {
        title: 'No budget goals yet',
        subAdmin: 'Click "+ Add Budget" to set your first spending limit.',
        subViewer: 'No budgets have been configured.',
      },
      card: {
        alertAt: 'Alert at {{pct}}% · Monthly',
        used: '{{pct}}% used',
        over: '${{amt}} over!',
        left: '${{amt}} left',
      },
      addButton: 'Add Budget Goal',
      performance: 'Budget Performance',
    },
    insights: {
      cards: {
        topSpend: 'Top Spending Category',
        monthlySavings: 'Monthly Savings',
        avgTx: 'Avg Transaction',
      },
      topSpendDesc: '${{amount}} total · {{pct}}% of expenses',
      savingsDesc: '{{rate}}% savings rate this month',
      avgDesc: 'Across {{count}} expense entries',
      chartTitle: 'Monthly Income vs Expenses',
      chartSub: '6-month comparison',
      spendDist: 'Spending Distribution',
    },
    recurring: {
      modal: {
        title: 'Add Recurring Transaction',
        descLabel: 'Description',
        descPlaceholder: 'e.g. Netflix',
        amountLabel: 'Amount (USD)',
        frequencyLabel: 'Frequency',
        typeLabel: 'Type',
        categoryLabel: 'Category',
        nextDue: 'Next Due Date',
        add: 'Add Recurring',
      },
      summary: {
        monthlyOut: 'Monthly Outflow',
        monthlyIn: 'Monthly Inflow',
        net: 'Net Recurring',
        activeSubs: '{{count}} active subscriptions',
        recurringIncome: '{{count}} recurring income',
        netPositive: 'Net positive',
        netOutflow: 'Net outflow',
      },
      title: 'Recurring Transactions',
      addButton: 'Add Recurring',
      empty: {
        title: 'No recurring transactions',
        sub: 'Add subscriptions and regular payments to track them here.',
      },
      pause: 'Pause',
      resume: 'Resume',
    },
    ai: {
      title: 'AI Financial Advisor',
      subtitle: 'Ask anything about your spending, savings, and financial health',
      chips: [
        "How is my savings rate?",
        'Where am I overspending?',
        'Give me a spending breakdown',
        'How can I save more?',
        'Compare my income vs expenses',
        'Am I on track this month?',
      ],
      askPlaceholder: 'Ask about your finances...',
      send: 'Send',
      connectingError: 'Sorry, I am having trouble connecting right now. Please try again.',
      processError: 'Sorry, I could not process that.',
      greeting: 'Hi! I am your AI financial advisor. I can see your April 2026 finances - you have earned ${{income}} and spent ${{expense}} so far. Ask me anything about your spending patterns, budget goals, or how to save more!',
      systemPrompts: {
        en: 'Answer in English.',
        hi: 'Answer in Hindi using simple, beginner-friendly language.',
        te: 'Answer in Telugu using simple, beginner-friendly language.',
      },
    },
  },
  hi: {
    common: {
      cancel: 'रद्द करें',
      save: 'सहेजें',
      saveChanges: 'परिवर्तन सहेजें',
      remove: 'हटाएं',
      edit: 'संपादित करें',
      deleteShort: 'हटाएं',
      allCategories: 'सभी श्रेणियां',
      allTypes: 'सभी प्रकार',
      income: 'आय',
      expense: 'खर्च',
      type: 'प्रकार',
      category: 'श्रेणी',
      amount: 'राशि',
      date: 'तारीख',
      description: 'विवरण',
      actions: 'कार्रवाई',
      monthly: 'मासिक',
      weekly: 'साप्ताहिक',
      yearly: 'वार्षिक',
      active: 'सक्रिय',
      paused: 'रुका हुआ',
      next: 'अगला',
    },
    sidebar: {
      appName: 'FinFlow',
      appSub: 'फाइनेंस ट्रैकर',
      menu: 'मेनू',
      nav: {
        dashboard: 'डैशबोर्ड',
        transactions: 'लेन-देन',
        insights: 'इनसाइट्स',
        budgets: 'बजट',
        recurring: 'आवर्ती',
        aiInsights: 'एआई इनसाइट्स',
      },
      role: 'भूमिका',
      admin: 'एडमिन',
      viewer: 'व्यूअर',
      adminAccess: 'एडमिन एक्सेस',
      viewOnly: 'केवल देखना',
      language: 'भाषा',
      languageNames: { en: 'English', hi: 'हिंदी', te: 'తెలుగు' },
    },
    topbar: {
      pages: {
        dashboard: { title: 'डैशबोर्ड', sub: 'अप्रैल 2026 · वित्तीय अवलोकन' },
        transactions: { title: 'लेन-देन', sub: 'अपने लेन-देन प्रबंधित करें' },
        insights: { title: 'इनसाइट्स', sub: 'खर्च विश्लेषण और पैटर्न' },
        budgets: { title: 'बजट लक्ष्य', sub: 'मासिक खर्च सीमा ट्रैक करें' },
        recurring: { title: 'आवर्ती', sub: 'सब्सक्रिप्शन और नियमित भुगतान प्रबंधित करें' },
        aiInsights: { title: 'एआई सलाहकार', sub: 'अपने एआई वित्तीय सलाहकार से चैट करें' },
      },
      export: 'एक्सपोर्ट',
      addTransaction: 'लेन-देन जोड़ें',
    },
    toasts: {
      roleAdmin: 'एडमिन मोड चालू',
      roleViewer: 'व्यूअर मोड चालू',
      csvExport: 'CSV एक्सपोर्ट हुआ',
      txUpdated: 'लेन-देन अपडेट हुआ',
      txAdded: 'लेन-देन जोड़ा गया',
      txDeleted: 'लेन-देन हटाया गया',
      budgetAdded: 'बजट लक्ष्य जोड़ा गया',
      budgetRemoved: 'बजट हटाया गया',
      recurringAdded: 'आवर्ती लेन-देन जोड़ा गया',
      recurringPaused: 'रोक दिया गया',
      recurringResumed: 'फिर से शुरू',
      recurringRemoved: 'हटाया गया',
    },
    dashboard: {
      labels: {
        balance: 'बैलेंस',
        income: 'आय',
        expense: 'खर्च',
        savingsRate: 'बचत दर',
        healthScore: 'हेल्थ स्कोर',
      },
      terms: {
        balance: 'बैलेंस वह राशि है जो आय से खर्च घटाने के बाद बचती है।',
        income: 'आय वह पैसा है जो अंदर आता है, जैसे वेतन या फ्रीलांस।',
        expense: 'खर्च वह पैसा है जो बाहर जाता है, जैसे बिल, खाना, यात्रा।',
        savingsRate: 'बचत दर आपकी आय का वह प्रतिशत है जो खर्च के बाद बचता है।',
      },
      cardNotes: {
        balancePositive: 'सकारात्मक नकद प्रवाह',
        balanceNegative: 'नकारात्मक नकद प्रवाह',
        incomeNote: 'इस महीने आई हुई राशि',
        expenseNote: 'इस महीने खर्च हुई राशि',
        savingsPositive: 'बचत बन रही है',
        savingsNegative: 'बचत पर दबाव है',
        healthNote: 'आय, खर्च और बचत के आधार पर',
      },
      interpretations: {
        balancePositive: 'इस महीने आपने खर्च से ज्यादा कमाया।',
        balanceNegative: 'इस महीने आपने कमाई से ज्यादा खर्च किया।',
        incomeStrong: 'इस महीने आय मजबूत है, आपके पास लचीलापन है।',
        incomeModest: 'इस महीने आय कम है, इसलिए बजट सावधानी से रखें।',
        expenseControlled: 'आय की तुलना में खर्च नियंत्रित है।',
        expenseHigh: 'खर्च अधिक है, लेकिन अभी आय के भीतर है।',
        expenseOver: 'खर्च आय से ऊपर है। गैर-जरूरी खर्च कम करें।',
        savingsGreat: 'आप अपनी आय का आधे से ज्यादा बचा रहे हैं। शानदार काम!',
        savingsGood: 'आप अच्छी बचत कर रहे हैं। ऐसे ही जारी रखें।',
        savingsLow: 'अभी बचत कम है। छोटे खर्चों में कटौती करें।',
        savingsNegative: 'इस महीने बचत नहीं हो रही क्योंकि खर्च आय से अधिक है।',
      },
      health: {
        healthy: 'आपकी वित्तीय स्थिति स्वस्थ है।',
        stable: 'आपकी वित्तीय स्थिति स्थिर है, और सुधार की गुंजाइश है।',
        overspending: 'आप इस महीने ज्यादा खर्च कर रहे हैं।',
      },
      chart: {
        balanceTrend: 'बैलेंस ट्रेंड',
        balanceSub: 'समय के साथ बैलेंस',
        spendCategory: 'श्रेणी अनुसार खर्च',
        currentMonth: 'चालू महीना',
        recentTransactions: 'हाल के लेन-देन',
      },
      table: {
        date: 'तारीख',
        description: 'विवरण',
        category: 'श्रेणी',
        type: 'प्रकार',
        amount: 'राशि',
      },
      legends: { balance: 'बैलेंस', income: 'आय', expenses: 'खर्च' },
    },
    transactions: {
      modal: {
        addTitle: 'लेन-देन जोड़ें',
        editTitle: 'लेन-देन संपादित करें',
        descLabel: 'विवरण',
        descPlaceholder: 'उदा. नेटफ्लिक्स सदस्यता',
        amountLabel: 'राशि (INR)',
        dateLabel: 'तारीख',
        typeLabel: 'प्रकार',
        categoryLabel: 'श्रेणी',
        saveAdd: 'लेन-देन सहेजें',
      },
      searchPlaceholder: 'लेन-देन खोजें...',
      sort: {
        newest: 'नवीनतम पहले',
        oldest: 'पुराने पहले',
        highest: 'सबसे अधिक राशि',
        lowest: 'सबसे कम राशि',
      },
      add: 'जोड़ें',
      empty: { title: 'कोई लेन-देन नहीं मिला', sub: 'आपके फिल्टर से कोई लेन-देन मेल नहीं खाता।' },
      showing: '{{total}} में से {{filtered}} लेन-देन दिख रहे हैं',
      headers: {
        date: 'तारीख',
        description: 'विवरण',
        category: 'श्रेणी',
        type: 'प्रकार',
        amount: 'राशि',
        actions: 'कार्रवाई',
      },
    },
    budgets: {
      modal: {
        title: 'बजट लक्ष्य जोड़ें',
        category: 'श्रेणी',
        allBudgeted: 'सभी श्रेणियों का बजट सेट है',
        monthlyLimit: 'मासिक सीमा (USD)',
        alertAt: 'अलर्ट (%)',
        save: 'बजट सहेजें',
      },
      empty: {
        title: 'अभी कोई बजट लक्ष्य नहीं',
        subAdmin: 'अपनी पहली खर्च सीमा सेट करने के लिए "+ बजट जोड़ें" क्लिक करें।',
        subViewer: 'कोई बजट कॉन्फ़िगर नहीं है।',
      },
      card: {
        alertAt: '{{pct}}% पर अलर्ट · मासिक',
        used: '{{pct}}% उपयोग हुआ',
        over: '${{amt}} अधिक!',
        left: '${{amt}} शेष',
      },
      addButton: 'बजट लक्ष्य जोड़ें',
      performance: 'बजट प्रदर्शन',
    },
    insights: {
      cards: {
        topSpend: 'शीर्ष खर्च श्रेणी',
        monthlySavings: 'मासिक बचत',
        avgTx: 'औसत लेन-देन',
      },
      topSpendDesc: 'कुल ${{amount}} · खर्च का {{pct}}%',
      savingsDesc: 'इस महीने बचत दर {{rate}}%',
      avgDesc: '{{count}} खर्च प्रविष्टियों में',
      chartTitle: 'मासिक आय बनाम खर्च',
      chartSub: '6 महीने की तुलना',
      spendDist: 'खर्च वितरण',
    },
    recurring: {
      modal: {
        title: 'आवर्ती लेन-देन जोड़ें',
        descLabel: 'विवरण',
        descPlaceholder: 'उदा. नेटफ्लिक्स',
        amountLabel: 'राशि (USD)',
        frequencyLabel: 'आवृत्ति',
        typeLabel: 'प्रकार',
        categoryLabel: 'श्रेणी',
        nextDue: 'अगली देय तिथि',
        add: 'आवर्ती जोड़ें',
      },
      summary: {
        monthlyOut: 'मासिक आउटफ्लो',
        monthlyIn: 'मासिक इनफ्लो',
        net: 'शुद्ध आवर्ती',
        activeSubs: '{{count}} सक्रिय सदस्यताएं',
        recurringIncome: '{{count}} आवर्ती आय',
        netPositive: 'शुद्ध सकारात्मक',
        netOutflow: 'शुद्ध आउटफ्लो',
      },
      title: 'आवर्ती लेन-देन',
      addButton: 'आवर्ती जोड़ें',
      empty: {
        title: 'कोई आवर्ती लेन-देन नहीं',
        sub: 'सदस्यता और नियमित भुगतान जोड़कर यहां ट्रैक करें।',
      },
      pause: 'रोकें',
      resume: 'फिर शुरू',
    },
    ai: {
      title: 'एआई वित्तीय सलाहकार',
      subtitle: 'अपने खर्च, बचत और वित्तीय स्वास्थ्य के बारे में पूछें',
      chips: [
        'मेरी बचत दर कैसी है?',
        'मैं कहां ज्यादा खर्च कर रहा हूं?',
        'मुझे खर्च का विभाजन बताएं',
        'मैं और कैसे बचत कर सकता हूं?',
        'आय और खर्च की तुलना करो',
        'क्या मैं इस महीने सही ट्रैक पर हूं?',
      ],
      askPlaceholder: 'अपने वित्त के बारे में पूछें...',
      send: 'भेजें',
      connectingError: 'माफ करें, अभी कनेक्शन में समस्या है। कृपया फिर प्रयास करें।',
      processError: 'माफ करें, मैं इसे प्रोसेस नहीं कर सका।',
      greeting: 'नमस्ते! मैं आपका एआई वित्तीय सलाहकार हूं। मैंने आपकी अप्रैल 2026 की स्थिति देखी - आपने अब तक ${{income}} कमाया और ${{expense}} खर्च किया है। अपने खर्च, बजट लक्ष्य, या बचत बढ़ाने के बारे में कुछ भी पूछें!',
      systemPrompts: {
        en: 'Answer in English.',
        hi: 'Answer in Hindi using simple, beginner-friendly language.',
        te: 'Answer in Telugu using simple, beginner-friendly language.',
      },
    },
  },
  te: {
    common: {
      cancel: 'రద్దు',
      save: 'సేవ్',
      saveChanges: 'మార్పులు సేవ్ చేయండి',
      remove: 'తొలగించు',
      edit: 'సవరించు',
      deleteShort: 'తొలగించు',
      allCategories: 'అన్ని వర్గాలు',
      allTypes: 'అన్ని రకాలూ',
      income: 'ఆదాయం',
      expense: 'ఖర్చు',
      type: 'రకం',
      category: 'వర్గం',
      amount: 'మొత్తం',
      date: 'తేదీ',
      description: 'వివరణ',
      actions: 'చర్యలు',
      monthly: 'మాసికం',
      weekly: 'వారంవారీ',
      yearly: 'వార్షికం',
      active: 'సక్రియం',
      paused: 'విరామం',
      next: 'తదుపరి',
    },
    sidebar: {
      appName: 'FinFlow',
      appSub: 'ఫైనాన్స్ ట్రాకర్',
      menu: 'మెను',
      nav: {
        dashboard: 'డాష్‌బోర్డ్',
        transactions: 'లావాదేవీలు',
        insights: 'ఇన్‌సైట్స్',
        budgets: 'బడ్జెట్లు',
        recurring: 'రికర్రింగ్',
        aiInsights: 'AI ఇన్‌సైట్స్',
      },
      role: 'పాత్ర',
      admin: 'అడ్మిన్',
      viewer: 'వ్యూయర్',
      adminAccess: 'అడ్మిన్ యాక్సెస్',
      viewOnly: 'చూడటానికి మాత్రమే',
      language: 'భాష',
      languageNames: { en: 'English', hi: 'हिंदी', te: 'తెలుగు' },
    },
    topbar: {
      pages: {
        dashboard: { title: 'డాష్‌బోర్డ్', sub: 'ఏప్రిల్ 2026 · ఆర్థిక అవలోకనం' },
        transactions: { title: 'లావాదేవీలు', sub: 'మీ లావాదేవీలను నిర్వహించండి' },
        insights: { title: 'ఇన్‌సైట్స్', sub: 'ఖర్చుల విశ్లేషణ మరియు నమూనాలు' },
        budgets: { title: 'బడ్జెట్ లక్ష్యాలు', sub: 'మాసిక ఖర్చు పరిమితులను ట్రాక్ చేయండి' },
        recurring: { title: 'రికర్రింగ్', sub: 'సబ్‌స్క్రిప్షన్లు మరియు పునరావృత చెల్లింపులు నిర్వహించండి' },
        aiInsights: { title: 'AI సలహాదారు', sub: 'మీ AI ఆర్థిక సలహాదారుతో చాట్ చేయండి' },
      },
      export: 'ఎగుమతి',
      addTransaction: 'లావాదేవీ జోడించు',
    },
    toasts: {
      roleAdmin: 'అడ్మిన్ మోడ్‌కు మారింది',
      roleViewer: 'వ్యూయర్ మోడ్‌కు మారింది',
      csvExport: 'CSV ఎగుమతి పూర్తైంది',
      txUpdated: 'లావాదేవీ నవీకరించబడింది',
      txAdded: 'లావాదేవీ జోడించబడింది',
      txDeleted: 'లావాదేవీ తొలగించబడింది',
      budgetAdded: 'బడ్జెట్ లక్ష్యం జోడించబడింది',
      budgetRemoved: 'బడ్జెట్ తొలగించబడింది',
      recurringAdded: 'రికర్రింగ్ లావాదేవీ జోడించబడింది',
      recurringPaused: 'విరామం',
      recurringResumed: 'మళ్లీ ప్రారంభం',
      recurringRemoved: 'తొలగించబడింది',
    },
    dashboard: {
      labels: {
        balance: 'బ్యాలెన్స్',
        income: 'ఆదాయం',
        expense: 'ఖర్చు',
        savingsRate: 'సేవింగ్స్ రేట్',
        healthScore: 'హెల్త్ స్కోర్',
      },
      terms: {
        balance: 'ఖర్చులు తీసివేసిన తరువాత మిగిలే డబ్బే బ్యాలెన్స్.',
        income: 'లోపలికి వచ్చే డబ్బు ఆదాయం; జీతం లేదా ఫ్రీలాన్స్ లాంటివి.',
        expense: 'బయటకు వెళ్లే డబ్బు ఖర్చు; బిల్లులు, ఆహారం, ప్రయాణం వంటి వాటి కోసం.',
        savingsRate: 'ఖర్చుల తర్వాత ఆదాయంలో ఎంత శాతం మిగిలిందో సేవింగ్స్ రేట్.',
      },
      cardNotes: {
        balancePositive: 'పాజిటివ్ క్యాష్ ఫ్లో',
        balanceNegative: 'నెగటివ్ క్యాష్ ఫ్లో',
        incomeNote: 'ఈ నెలలో వచ్చిన డబ్బు',
        expenseNote: 'ఈ నెలలో ఖర్చైన డబ్బు',
        savingsPositive: 'సేవింగ్స్ పెరుగుతున్నాయి',
        savingsNegative: 'సేవింగ్స్‌పై ఒత్తిడి ఉంది',
        healthNote: 'ఆదాయం, ఖర్చు, సేవింగ్స్ ఆధారంగా',
      },
      interpretations: {
        balancePositive: 'ఈ నెల మీరు ఖర్చుకంటే ఎక్కువ సంపాదించారు.',
        balanceNegative: 'ఈ నెల మీరు ఆదాయంకంటే ఎక్కువ ఖర్చు చేశారు.',
        incomeStrong: 'ఈ నెల ఆదాయం బలంగా ఉంది, మీకు సౌలభ్యం ఉంది.',
        incomeModest: 'ఈ నెల ఆదాయం తక్కువగా ఉంది, కాబట్టి జాగ్రత్తగా బడ్జెట్ చేయండి.',
        expenseControlled: 'ఆదాయంతో పోలిస్తే మీ ఖర్చు నియంత్రణలో ఉంది.',
        expenseHigh: 'ఖర్చు ఎక్కువగా ఉంది, కానీ ఇంకా ఆదాయంలోనే ఉంది.',
        expenseOver: 'ఖర్చు ఆదాయాన్ని మించి ఉంది. అవసరం లేని ఖర్చులు తగ్గించండి.',
        savingsGreat: 'మీ ఆదాయంలో సగానికి మించి సేవ్ చేస్తున్నారు. అద్భుతం!',
        savingsGood: 'మీరు మంచి భాగాన్ని సేవ్ చేస్తున్నారు. అలాగే కొనసాగండి.',
        savingsLow: 'ప్రస్తుతం సేవింగ్స్ తక్కువగా ఉన్నాయి. చిన్న ఖర్చులను తగ్గించండి.',
        savingsNegative: 'ఈ నెల ఖర్చు ఆదాయాన్ని మించడంతో సేవింగ్స్ జరగడం లేదు.',
      },
      health: {
        healthy: 'మీ ఆర్థిక పరిస్థితి ఆరోగ్యంగా ఉంది.',
        stable: 'మీ ఆర్థిక స్థితి స్థిరంగా ఉంది, ఇంకా మెరుగుపరచవచ్చు.',
        overspending: 'ఈ నెల మీరు అధికంగా ఖర్చు చేస్తున్నారు.',
      },
      chart: {
        balanceTrend: 'బ్యాలెన్స్ ట్రెండ్',
        balanceSub: 'కాలక్రమంలో బ్యాలెన్స్',
        spendCategory: 'వర్గాల వారీ ఖర్చు',
        currentMonth: 'ప్రస్తుత నెల',
        recentTransactions: 'ఇటీవలి లావాదేవీలు',
      },
      table: {
        date: 'తేదీ',
        description: 'వివరణ',
        category: 'వర్గం',
        type: 'రకం',
        amount: 'మొత్తం',
      },
      legends: { balance: 'బ్యాలెన్స్', income: 'ఆదాయం', expenses: 'ఖర్చులు' },
    },
    transactions: {
      modal: {
        addTitle: 'లావాదేవీ జోడించండి',
        editTitle: 'లావాదేవీ సవరించండి',
        descLabel: 'వివరణ',
        descPlaceholder: 'ఉదా. నెట్‌ఫ్లిక్స్ సభ్యత్వం',
        amountLabel: 'మొత్తం (INR)',
        dateLabel: 'తేదీ',
        typeLabel: 'రకం',
        categoryLabel: 'వర్గం',
        saveAdd: 'లావాదేవీ సేవ్ చేయండి',
      },
      searchPlaceholder: 'లావాదేవీలు వెతకండి...',
      sort: {
        newest: 'కొత్తవి ముందు',
        oldest: 'పాతవి ముందు',
        highest: 'అత్యధిక మొత్తం',
        lowest: 'అత్యల్ప మొత్తం',
      },
      add: 'జోడించు',
      empty: { title: 'లావాదేవీలు లేవు', sub: 'మీ ఫిల్టర్లకు సరిపడే లావాదేవీలు లేవు.' },
      showing: 'మొత్తం {{total}} లో {{filtered}} లావాదేవీలు చూపుతున్నాం',
      headers: {
        date: 'తేదీ',
        description: 'వివరణ',
        category: 'వర్గం',
        type: 'రకం',
        amount: 'మొత్తం',
        actions: 'చర్యలు',
      },
    },
    budgets: {
      modal: {
        title: 'బడ్జెట్ లక్ష్యం జోడించండి',
        category: 'వర్గం',
        allBudgeted: 'అన్ని వర్గాలకు బడ్జెట్ ఉంది',
        monthlyLimit: 'మాసిక పరిమితి (USD)',
        alertAt: 'అలర్ట్ శాతం (%)',
        save: 'బడ్జెట్ సేవ్',
      },
      empty: {
        title: 'ఇంకా బడ్జెట్ లక్ష్యాలు లేవు',
        subAdmin: 'మీ మొదటి ఖర్చు పరిమితి కోసం "+ బడ్జెట్ జోడించు" క్లిక్ చేయండి.',
        subViewer: 'ఏ బడ్జెట్లు సెటప్ చేయలేదు.',
      },
      card: {
        alertAt: '{{pct}}% వద్ద అలర్ట్ · మాసికం',
        used: '{{pct}}% ఉపయోగించారు',
        over: '${{amt}} అధికం!',
        left: '${{amt}} మిగిలింది',
      },
      addButton: 'బడ్జెట్ లక్ష్యం జోడించు',
      performance: 'బడ్జెట్ పనితీరు',
    },
    insights: {
      cards: {
        topSpend: 'అత్యధిక ఖర్చు వర్గం',
        monthlySavings: 'మాసిక సేవింగ్స్',
        avgTx: 'సగటు లావాదేవీ',
      },
      topSpendDesc: 'మొత్తం ${{amount}} · ఖర్చులో {{pct}}%',
      savingsDesc: 'ఈ నెల సేవింగ్స్ రేట్ {{rate}}%',
      avgDesc: '{{count}} ఖర్చు ఎంట్రీలలో',
      chartTitle: 'మాసిక ఆదాయం vs ఖర్చులు',
      chartSub: '6 నెలల పోలిక',
      spendDist: 'ఖర్చుల పంపిణీ',
    },
    recurring: {
      modal: {
        title: 'రికర్రింగ్ లావాదేవీ జోడించండి',
        descLabel: 'వివరణ',
        descPlaceholder: 'ఉదా. నెట్‌ఫ్లిక్స్',
        amountLabel: 'మొత్తం (USD)',
        frequencyLabel: 'ఆవర్తనం',
        typeLabel: 'రకం',
        categoryLabel: 'వర్గం',
        nextDue: 'తదుపరి చెల్లింపు తేదీ',
        add: 'రికర్రింగ్ జోడించు',
      },
      summary: {
        monthlyOut: 'మాసిక ఔట్‌ఫ్లో',
        monthlyIn: 'మాసిక ఇన్‌ఫ్లో',
        net: 'నెట్ రికర్రింగ్',
        activeSubs: '{{count}} సక్రియ సబ్‌స్క్రిప్షన్లు',
        recurringIncome: '{{count}} రికర్రింగ్ ఆదాయం',
        netPositive: 'నెట్ పాజిటివ్',
        netOutflow: 'నెట్ ఔట్‌ఫ్లో',
      },
      title: 'రికర్రింగ్ లావాదేవీలు',
      addButton: 'రికర్రింగ్ జోడించు',
      empty: {
        title: 'రికర్రింగ్ లావాదేవీలు లేవు',
        sub: 'సబ్‌స్క్రిప్షన్లు మరియు రెగ్యులర్ చెల్లింపులను ఇక్కడ ట్రాక్ చేయండి.',
      },
      pause: 'విరామం',
      resume: 'మళ్లీ ప్రారంభించు',
    },
    ai: {
      title: 'AI ఆర్థిక సలహాదారు',
      subtitle: 'మీ ఖర్చు, సేవింగ్స్ మరియు ఆర్థిక ఆరోగ్యం గురించి అడగండి',
      chips: [
        'నా సేవింగ్స్ రేట్ ఎలా ఉంది?',
        'నేను ఎక్కడ ఎక్కువ ఖర్చు చేస్తున్నాను?',
        'నా ఖర్చుల విభజన చెప్పు',
        'నేను ఇంకా ఎలా సేవ్ చేయగలను?',
        'ఆదాయం మరియు ఖర్చులను పోల్చు',
        'ఈ నెల నేను ట్రాక్‌లో ఉన్నానా?',
      ],
      askPlaceholder: 'మీ ఆర్థిక విషయాల గురించి అడగండి...',
      send: 'పంపు',
      connectingError: 'క్షమించండి, కనెక్షన్ సమస్య ఉంది. మళ్లీ ప్రయత్నించండి.',
      processError: 'క్షమించండి, దాన్ని ప్రాసెస్ చేయలేకపోయాను.',
      greeting: 'హాయ్! నేను మీ AI ఆర్థిక సలహాదారు. మీ ఏప్రిల్ 2026 ఆర్థిక వివరాలు నాకు కనిపిస్తున్నాయి - మీరు ఇప్పటివరకు ${{income}} సంపాదించారు మరియు ${{expense}} ఖర్చు చేశారు. మీ ఖర్చు అలవాట్లు, బడ్జెట్ లక్ష్యాలు, లేదా ఎలా సేవ్ చేయాలో అడగండి!',
      systemPrompts: {
        en: 'Answer in English.',
        hi: 'Answer in Hindi using simple, beginner-friendly language.',
        te: 'Answer in Telugu using simple, beginner-friendly language.',
      },
    },
  },
};
